import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useMineMutate, useRepairMutate } from 'api/dashboardApi';
import ActiveCardInfo from 'components/UI/ActiveCardInfo';
import CustomImgButton from 'components/UI/CustomImgButton';
import VerticalCarousel from 'components/UI/VerticalCarousel/VerticalCarousel';
import useTimer from 'hooks/useTimer';
import './style.scss';

const MineAndRepair = ({ items, onCardSelect, onCardUpdate, toggleModal }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const orderNo = searchParams.get('orderNo');
    const batch = searchParams.get('batch');
    if (!items?.length || !orderNo || !batch) return;
    const idx = items.findIndex((item) => String(item.orderNo) === orderNo && String(item.batch) === batch);
    if (idx !== -1) setActiveIndex(idx);
  }, [searchParams, items]);

  const handleSelectIndex = (idx) => {
    setActiveIndex(idx);
    onCardSelect?.(items?.[idx]);
  };

  const activeCard = items?.[activeIndex];

  const { isRunning: isMining } = useTimer({
    cardId: activeCard?.assetId,
    mineCycleStartedAt: activeCard?.mineCycleStartedAt,
    miningTime: activeCard?.miningTime,
  });

  const mineMutation = useMineMutate();
  const repairMutation = useRepairMutate();

  const handleMine = () => {
    const { assetId, batch } = activeCard;
    mineMutation.mutate(
      { assetId, batch },
      {
        onSuccess: (response) => {
          onCardUpdate?.(response?.data);
          toggleModal(
            t('mineAndRepair.minedMessage', { quantity: activeCard?.miningQuantity, pair: activeCard?.miningPair }),
            '',
          );
        },
        onError: (error) => {
          queryClient.invalidateQueries({ queryKey: ['home-corausel'] });
          toggleModal(t('mineAndRepair.errorTitle'), error?.response?.data?.message || t('mineAndRepair.miningErrorFallback'), '');
        },
      },
    );
  };

  const handleRepair = () => {
    const { assetId, batch } = activeCard;
    repairMutation.mutate(
      { assetId, batch },
      {
        onSuccess: (response) => onCardUpdate?.(response?.data),
        onError: (error) =>
          toggleModal(t('mineAndRepair.errorTitle'), error?.response?.data?.message || t('mineAndRepair.repairErrorFallback'), ''),
      },
    );
  };

  return (
    <div className="mine-repair-wrapper">
      <div className="mine-repair-row">
        <VerticalCarousel activeIndex={activeIndex} items={items} setActiveIndex={handleSelectIndex} />
        <ActiveCardInfo activeCard={activeCard} isCostInfoExist={false}>
          <div className="mine-repair-actions">
            <CustomImgButton disabled={isMining} onClose={handleMine} title={t('mineAndRepair.mine')} />
            <CustomImgButton
              disabled={!activeCard?.requiredForRepairQuantity}
              onClose={handleRepair}
              title={t('mineAndRepair.repair')}
            />
          </div>
        </ActiveCardInfo>
      </div>
    </div>
  );
};

export default MineAndRepair;
